/**
 * Configuration management for the Congrs web application
 * Handles environment-specific settings and secrets
 */

// Define environment values
export const ENV = {
  LOCAL: "local",
  DEV: "development",
  PROD: "production",
};

/**
 * Safely retrieves environment variables across Node and browser contexts
 * @param {string} key - Environment variable name
 * @param {any} defaultValue - Default value if not found
 * @returns {any} The environment variable value or default
 */
function getEnv(key, defaultValue = undefined) {
  // Node.js environment (build time)
  if (typeof process !== "undefined" && process.env && process.env[key]) {
    return process.env[key];
  }

  // Browser environment (runtime with Vite)
  if (
    typeof import.meta !== "undefined" &&
    import.meta.env &&
    import.meta.env[key]
  ) {
    return import.meta.env[key];
  }

  return defaultValue;
}

/**
 * Determine current environment
 */
const currentEnv = getEnv("VITE_CLOUD_ENV", getEnv("CLOUD_ENV", ENV.LOCAL));
const LOCAL_IP = getEnv("VITE_LOCAL_IP");

/**
 * Environment-specific URLs
 */
const environmentConfig = {
  [ENV.LOCAL]: {
    baseUrl:
      getEnv("VITE_BASE_URL") ||
      (LOCAL_IP ? `http://${LOCAL_IP}:5173` : "http://localhost:5173"),
    apiUrl:
      getEnv("VITE_API_URL") ||
      (LOCAL_IP ? `http://${LOCAL_IP}:3000/api` : "http://localhost:3000/api"),
    mobileScheme: "congrs-dev",
    // Expo's own dev server, started separately (`pnpm --filter mobile-next
    // web`). Deployed environments serve the same app from /mobile on this
    // host — see deploy/congrs-mobile-next.dockerfile.
    mobileWebUrl: LOCAL_IP
      ? `http://${LOCAL_IP}:8081`
      : "http://localhost:8081",
  },
  [ENV.DEV]: {
    baseUrl: "https://app.dev.congrs.com",
    apiUrl: "https://app.dev.congrs.com/api",
    mobileScheme: "congrs-preview",
    mobileWebUrl: "/mobile",
  },
  [ENV.PROD]: {
    baseUrl: "https://app.congrs.com",
    apiUrl: "https://app.congrs.com/api",
    mobileScheme: "congrs",
    mobileWebUrl: "/mobile",
  },
};

/**
 * Service configurations with environment-based secrets
 */
const serviceConfig = {
  posthog: {
    key: getEnv("VITE_POSTHOG_KEY", ""),
    host: getEnv("VITE_POSTHOG_HOST", "https://eu.posthog.com"),
  },

  OAuth: {
    webClientId: getEnv(
      "VITE_GOOGLE_CLIENT_ID",
      "224661272614-hlo7jjo76b9g4bpjuj9kfudt75nvd26i.apps.googleusercontent.com"
    ),
  },

  sentry: {
    dsn: getEnv("VITE_SENTRY_DSN"),
  },
};

/**
 * Build and export the final configuration
 */
export const webConfig = {
  // Combine configs
  ...environmentConfig[currentEnv],
  ...serviceConfig,

  environment: currentEnv,

  // Environment flags for conditional logic
  isLocal: currentEnv === ENV.LOCAL,
  isDevelopment: currentEnv === ENV.DEV,
  isProduction: currentEnv === ENV.PROD,
};
